import { Grid, Radio, RadioGroup, useEventCallback } from '@mui/material';
import { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { LastStepVM, SectionVM, ParentVM } from 'civil-status/view-models/step-form';
import { DatePicker } from '@mui/lab';
import { FormControlLabel } from '@mui/material';
import { Checkbox } from '@mui/material';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'civil-status/view-models/accordion-generality-form';
import { FormControl } from '@mui/material';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';
import { KeySubjects } from 'civil-status/constants/keys-subjects-section';

interface SeparationDataProps {
  formContext: FormikProps<LastStepVM>;
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  configuration?: string;
  controllLawyerHusbandWife: (isLawyerHusband?: boolean, isLawyerWife?: boolean) => void;
  controllTypeSeparation: (typeSeparation?: string) => void;
  controllIsLawyerHusbandWife: (isLawyerHusbandWife?: boolean) => void;
  setDataInterprete: (isWithInterprete?: boolean) => void;
}

const SeparationDataComponent = ({
  formContext,
  disabled = false,
  values,
  errors,
  touched,
  configuration,
  onChange,
  onChanges,
  controllLawyerHusbandWife,
  controllTypeSeparation,
  controllIsLawyerHusbandWife,
  setDataInterprete,
}: SeparationDataProps): React.ReactElement => {
  const isSepar01: boolean = useMemo(() => {
    return configuration === ConfigurationType.SEPAR01;
  }, [configuration]);

  const isSepar02: boolean = useMemo(() => {
    return configuration === ConfigurationType.SEPAR02;
  }, [configuration]);

  const isSepar03: boolean = useMemo(() => {
    return configuration === ConfigurationType.SEPAR03;
  }, [configuration]);

  const isSepar04: boolean = useMemo(() => {
    return configuration === ConfigurationType.SEPAR04;
  }, [configuration]);

  const isSepar08: boolean = useMemo(() => {
    return configuration === ConfigurationType.SEPAR08;
  }, [configuration]);

  const isUcdtsu01: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSU01;
  }, [configuration]);

  const isUcdtsu02: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSU02;
  }, [configuration]);

  const isUcdtsu03: boolean = useMemo(() => {
    return configuration === ConfigurationType.UCDTSU03;
  }, [configuration]);

  useEffect(() => {
    controllLawyerHusbandWife(values?.isLawyerHusband);
  }, [values?.isLawyerHusband]);

  useEffect(() => {
    controllLawyerHusbandWife(undefined, values?.isLawyerWife);
  }, [values?.isLawyerWife]);

  useEffect(() => {
    controllTypeSeparation(values?.typeSeparation);
  }, [values?.typeSeparation]);

  useEffect(() => {
    controllIsLawyerHusbandWife(values?.isLawyerHusbandWife);
  }, [values?.isLawyerHusbandWife]);

  useEffect(() => {
    setDataInterprete(values?.isWithInterprete);
  }, [values?.isWithInterprete]);

  const onChangeItalian = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values || {}).italian,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onChangesItalian = useCallback(
    (valuesNew) => {
      onChange('italian', {
        ...(values || {}).italian,
        ...valuesNew,
      });
    },
    [values, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values || {}).foreign,
        [key]: value,
      });
    },
    [values, onChange]
  );

  const onFieldsForeignChange = useCallback(
    (valuesNew) => {
      onChange('foreign', {
        ...(valuesNew || {}),
      });
    },
    [values, onChange]
  );

  const handleListTypeAgreement = useEventCallback((item?: ItemVM) => {
    onChange('typeAgreement', item?.id?.toString());
  });

  const handleTypeSeparation = useEventCallback((item?: ItemVM) => {
    onChange('typeSeparation', item?.id?.toString());
  });

  const handleTypeEvent = useEventCallback((item?: ItemVM) => {
    onChange('typeEvent', item?.id?.toString());
  });

  useEffect(() => {
    if (!isSepar08) {
      return;
    }

    formContext.setValues({
      ...formContext.values,
      [KeySubjects.keySposo]: {
        ...(formContext.values[KeySubjects.keySposo] || {}),
        flagFirmatario: values?.isHusband ? '1' : '0',
      } as ParentVM,
      [KeySubjects.keySposa]: {
        ...(formContext.values[KeySubjects.keySposa] || {}),
        flagFirmatario: values?.isWife ? '1' : '0',
      } as ParentVM,
    });
  }, [values?.isHusband, values?.isWife, isSepar08]);

  return (
    <Grid container spacing={2} item>
      {isSepar03 || isSepar08 || isUcdtsu02 || isUcdtsu03 ? undefined : (
        <Fragment>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isMaintenanceAllowance"
                  checked={values?.isMaintenanceAllowance || false}
                  onChange={() => {
                    onChanges({
                      ...(values || {}),
                      isMaintenanceAllowance: !values?.isMaintenanceAllowance,
                      maintenanceAllowance: '',
                    });
                  }}
                  inputProps={{
                    'aria-label': isUcdtsu01
                      ? 'Assegno mantenimento'
                      : 'Assegno mantenimento/divorzile',
                  }}
                />
              }
              label={isUcdtsu01 ? 'Assegno mantenimento' : 'Assegno mantenimento/divorzile'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label={
                isUcdtsu01
                  ? `Assegno di mantenimento ${values?.isMaintenanceAllowance ? '*' : ''}`
                  : `Assegno di mantenimento/divorzile ${values?.isMaintenanceAllowance ? '*' : ''}`
              }
              aria-label={
                isUcdtsu01 ? 'Assegno di mantenimento' : 'Assegno di mantenimento/divorzile'
              }
              id="maintenanceAllowance"
              name="maintenanceAllowance"
              fullWidth
              disabled={disabled || !values?.isMaintenanceAllowance}
              helperText={errors?.maintenanceAllowance}
              error={touched?.maintenanceAllowance && Boolean(errors?.maintenanceAllowance)}
              type="text"
              variant="outlined"
              value={values?.maintenanceAllowance || ''}
              onChange={(event) => {
                onChange('maintenanceAllowance', event.target.value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isPendingJudgment"
                  checked={values?.isPendingJudgment || false}
                  onChange={() => {
                    onChanges({
                      ...(values || {}),
                      isPendingJudgment: !values?.isPendingJudgment,
                      judicialAuthority: '',
                    });
                  }}
                  inputProps={{ 'aria-label': 'Parte in causa in giudizio pendente' }}
                />
              }
              label="Parte in causa in giudizio pendente"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label={`Autorità giudiziale ${values?.isPendingJudgment ? '*' : ''}`}
              aria-label="Autorità giudiziale"
              id="judicialAuthority"
              name="judicialAuthority"
              fullWidth
              disabled={disabled || !values?.isPendingJudgment}
              helperText={errors?.judicialAuthority}
              error={touched?.judicialAuthority && Boolean(errors?.judicialAuthority)}
              type="text"
              variant="outlined"
              value={values?.judicialAuthority || ''}
              onChange={(event) => {
                onChange('judicialAuthority', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      )}
      {isSepar08 || isUcdtsu03 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isHusband"
                  checked={values?.isHusband || false}
                  disabled={values?.isWife}
                  onChange={() => {
                    onChanges({
                      ...(values || {}),
                      isHusband: !values?.isHusband,
                      isWife: false,
                      isLawyerHusband: false,
                      isLawyerWife: false,
                    });
                  }}
                  inputProps={{
                    'aria-label': isUcdtsu03 ? 'Presenza coniuge 1' : 'Presenza marito',
                  }}
                />
              }
              label={isUcdtsu03 ? 'Presenza coniuge 1' : 'Presenza marito'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isWife"
                  checked={values?.isWife || false}
                  disabled={values?.isHusband}
                  onChange={() => {
                    onChanges({
                      ...(values || {}),
                      isWife: !values?.isWife,
                      isHusband: false,
                      isLawyerHusband: false,
                      isLawyerWife: false,
                    });
                  }}
                  inputProps={{
                    'aria-label': isUcdtsu03 ? 'Presenza coniuge 2' : 'Presenza moglie',
                  }}
                />
              }
              label={isUcdtsu03 ? 'Presenza coniuge 2' : 'Presenza moglie'}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      <Grid container spacing={2} item>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isLawyerHusband"
                checked={values?.isLawyerHusband || false}
                disabled={
                  isSepar08 || isUcdtsu03
                    ? !values?.isHusband
                    : disabled || values?.isLawyerHusbandWife
                }
                onChange={() => {
                  onChange('isLawyerHusband', !values?.isLawyerHusband);
                }}
                inputProps={{
                  'aria-label':
                    isUcdtsu01 || isUcdtsu02 || isUcdtsu03
                      ? 'Presenza avvocato coniuge 1'
                      : 'Presenza avvocato marito',
                }}
              />
            }
            label={
              isUcdtsu01 || isUcdtsu02 || isUcdtsu03
                ? 'Presenza avvocato coniuge 1'
                : 'Presenza avvocato marito'
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isLawyerWife"
                checked={values?.isLawyerWife || false}
                disabled={
                  isSepar08 || isUcdtsu03
                    ? !values?.isWife
                    : disabled || values?.isLawyerHusbandWife
                }
                onChange={() => {
                  onChange('isLawyerWife', !values?.isLawyerWife);
                }}
                inputProps={{
                  'aria-label':
                    isUcdtsu01 || isUcdtsu02 || isUcdtsu03
                      ? 'Presenza avvocato coniuge 2'
                      : 'Presenza avvocato moglie',
                }}
              />
            }
            label={
              isUcdtsu01 || isUcdtsu02 || isUcdtsu03
                ? 'Presenza avvocato coniuge 2'
                : 'Presenza avvocato moglie'
            }
          />
        </Grid>
        {isSepar01 || isSepar02 || isSepar03 || isSepar04 ? (
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="isLawyerHusbandWife"
                  checked={values?.isLawyerHusbandWife || false}
                  onChange={() => {
                    onChange('isLawyerHusbandWife', !values?.isLawyerHusbandWife);
                  }}
                  inputProps={{ 'aria-label': 'Avvocato marito e moglie' }}
                  disabled={disabled || values?.isLawyerWife || values?.isLawyerHusband}
                />
              }
              label="Avvocato marito e moglie"
            />
          </Grid>
        ) : undefined}
      </Grid>
      {isSepar03 || isSepar08 || isUcdtsu02 || isUcdtsu03 ? undefined : (
        <Fragment>
          <Grid item xs={12} md={4}>
            <DatePicker
              label={isUcdtsu01 ? 'Data unione civile' : 'Data evento matrimonio *'}
              aria-label={isUcdtsu01 ? 'Data unione civile' : 'Data evento matrimonio'}
              value={values?.dateMarriage || null}
              onChange={(date) => {
                onChange('dateMarriage', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateMarriage"
                  fullWidth
                  helperText={errors?.dateMarriage}
                  error={touched?.dateMarriage && Boolean(errors?.dateMarriage)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Data conferma accordo *"
              aria-label="Data conferma accordo"
              value={values?.dateAgreementConfirmation || null}
              onChange={(date) => {
                onChange('dateAgreementConfirmation', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateAgreementConfirmation"
                  fullWidth
                  helperText={errors?.dateAgreementConfirmation}
                  error={
                    touched?.dateAgreementConfirmation && Boolean(errors?.dateAgreementConfirmation)
                  }
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Stato"
                id="isForeign"
                value={values?.isForeign || 'false'}
                onChange={() => {
                  onChanges({
                    ...(values || {}),
                    isForeign: !values?.isForeign,
                    italian: {
                      location: '',
                      province: '',
                      municipality: null,
                    },
                    foreign: {
                      state: null,
                      province: '',
                      municipality: null,
                    },
                  });
                }}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Italia"
                  disabled={disabled}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Estero"
                  disabled={disabled}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {!values?.isForeign ? (
            <Italian
              disabledComponent={disabled}
              values={values?.italian}
              onChange={onChangeItalian}
              onChanges={onChangesItalian}
              errors={errors?.italian as FormikErrors<ItalianForm>}
              touched={touched?.italian as unknown as FormikTouched<ItalianForm>}
              labelMunicipality="Comune ente"
              labelProvince="Provincia ente"
              required={!isUcdtsu01}
              fromDate={values?.dataStart || values?.dateMarriage || null}
            />
          ) : (
            <Foreign
              disabledComponent={disabled}
              values={values?.foreign}
              onChange={onFieldForeignChange}
              onChanges={onFieldsForeignChange}
              labelState="Stato ente"
              errors={errors?.foreign as FormikErrors<ForeignForm>}
              touched={touched?.foreign as unknown as FormikTouched<ForeignForm>}
              required={!isUcdtsu01}
              isWithLocationForeign={isSepar01 || isSepar02 || isSepar04 || isUcdtsu01}
            />
          )}
          <Grid item xs={12} md={6}>
            <PmiTextField
              label={`Celebrato da ${isUcdtsu01 ? '' : '*'}`}
              aria-label="Celebrato da"
              id="celebratedBy"
              name="celebratedBy"
              fullWidth
              disabled={disabled}
              helperText={errors?.celebratedBy}
              error={touched?.celebratedBy && Boolean(errors?.celebratedBy)}
              type="text"
              variant="outlined"
              value={values?.celebratedBy || ''}
              onChange={(event) => {
                onChange('celebratedBy', event.target.value);
              }}
            />
          </Grid>
        </Fragment>
      )}
      {isSepar02 || isSepar03 || isSepar08 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Tipo accordo"
            idDominio="53"
            value={values?.typeAgreement || ''}
            onChange={handleListTypeAgreement}
            disabled={disabled}
            error={Boolean(errors?.typeAgreement) && touched?.typeAgreement}
            betweenFrom={isSepar03 || isSepar08 ? 1 : 2}
            betweenTo={3}
            required
          />
        </Grid>
      ) : undefined}
      {isSepar02 || isSepar04 ? (
        <Grid item xs={12} md={6}>
          <ListAnsc
            label="Tipo separazione"
            idDominio="104"
            value={values?.typeSeparation || ''}
            onChange={handleTypeSeparation}
            disabled={disabled}
            error={Boolean(errors?.typeSeparation) && touched?.typeSeparation}
            withoutId={isSepar02 ? ['4'] : undefined}
            required
          />
        </Grid>
      ) : undefined}
      {isSepar04 ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ListAnsc
              label="Tipo evento"
              idDominio="106"
              value={values?.typeEvent || ''}
              onChange={handleTypeEvent}
              disabled={disabled}
              error={Boolean(errors?.typeEvent) && touched?.typeEvent}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              label="Riassunto della dichiarazione *"
              aria-label="Riassunto della dichiarazione *"
              id="templateTranscriptionText"
              name="templateTranscriptionText"
              fullWidth
              type="text"
              variant="outlined"
              multiline
              rows={10}
              value={values?.templateTranscriptionText || ''}
              onChange={(event) => {
                onChange('templateTranscriptionText', event.target.value);
              }}
              error={
                touched?.templateTranscriptionText && Boolean(errors?.templateTranscriptionText)
              }
              helperText={errors?.templateTranscriptionText}
              disabled={disabled}
            />
          </Grid>
        </Fragment>
      ) : undefined}
      {isUcdtsu01 || isUcdtsu02 || isUcdtsu03 || isSepar01 || isSepar02 || isSepar03 ? (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isWithInterprete"
                checked={values?.isWithInterprete || false}
                onChange={() => {
                  onChange('isWithInterprete', !values?.isWithInterprete);
                }}
                inputProps={{ 'aria-label': 'Presenza interprete' }}
                disabled={disabled}
              />
            }
            label="Presenza interprete"
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

SeparationDataComponent.displayName = 'Form Separation Data component';

const SeparationData = memo(SeparationDataComponent);

export default SeparationData;
